/*
MIT License

Copyright (c) 2021 SEEK

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/
// Copied from https://github.com/vanilla-extract-css/vanilla-extract/blob/%40vanilla-extract/css%401.11.0/site/src/themeUtils.ts
import { StyleRule } from '@vanilla-extract/css';
import mapValues from 'lodash-es/mapValues';
import omit from 'lodash-es/omit';
import isEqual from 'react-fast-compare';

// Referenced from https://viagogo.visualstudio.com/Packages/_git/Viagogo.Npm.Core?path=/src/themes/shared/base/media-queries.ts
export const breakpoints = {
  mobile: 0,
  tablet: 960,
  desktop: 1200,
  largeDesktop: 1600,
};

type CSSProps = Omit<StyleRule, '@media' | '@supports'>;

const queries = mapValues(
  omit(breakpoints, 'mobile'),
  (bp) => `screen and (min-width: ${bp}px)`
);

const makeMediaQuery =
  (breakpoint: keyof typeof queries) => (styles: CSSProps) =>
    !styles || Object.keys(styles).length === 0
      ? {}
      : {
          [queries[breakpoint]]: styles,
        };

const mediaQuery = {
  tablet: makeMediaQuery('tablet'),
  desktop: makeMediaQuery('desktop'),
  largeDesktop: makeMediaQuery('largeDesktop'),
};

interface ResponsiveStyle {
  mobile?: CSSProps;
  tablet?: CSSProps;
  desktop?: CSSProps;
  largeDesktop?: CSSProps;
}

/**
 * This function sets the styles for the minimum width, where mobile is 0 (and default)
 * Mobile first helper function for responsive styles.
 *
 * Set mobile styles, then override with larger screen sizes.
 * @param styles
 * @returns
 */
export const responsiveStyle = ({
  mobile,
  tablet,
  desktop,
  largeDesktop,
}: ResponsiveStyle): StyleRule => {
  const mobileStyles = omit(mobile, '@media');

  const tabletStyles = !tablet || isEqual(tablet, mobileStyles) ? null : tablet;

  const stylesBelowDesktop = tabletStyles || mobileStyles;

  const desktopStyles =
    !desktop || isEqual(desktop, stylesBelowDesktop) ? null : desktop;

  const stylesBelowLargeDesktop = desktopStyles || tabletStyles || mobileStyles;

  const largeDesktopStyles =
    !largeDesktop || isEqual(largeDesktop, stylesBelowLargeDesktop)
      ? null
      : largeDesktop;

  const hasMediaQueries = tabletStyles || desktopStyles || largeDesktopStyles;

  return {
    ...mobileStyles,
    ...(hasMediaQueries
      ? {
          '@media': {
            ...(tabletStyles ? mediaQuery.tablet(tabletStyles) : {}),
            ...(desktopStyles ? mediaQuery.desktop(desktopStyles) : {}),
            ...(largeDesktopStyles
              ? mediaQuery.largeDesktop(largeDesktopStyles)
              : {}),
          },
        }
      : {}),
  };
};
